import { useCurrentPlanApi } from "@circle-react/components/SettingsApp/Plan/hooks/useCurrentPlanApi";
import { useMutation } from "react-query";

import { accountSubscriptionApi } from "@/react/api/accountSubscriptionApi";

import { UPGRADE_PATHS } from "../constants";
import { useUpgradePlanContext } from "../UpgradePlanContext";
import { useUpgradePlanNavigate } from "../upgradePlanRouterHelpers";

interface UseAccountSubscriptionApiOptions {
  source?: string;
  usedForWorkflows?: boolean;
}

interface UpdateSubscriptionParams {
  planCouponId?: number;
  planId: number;
}

export const useAccountSubscriptionApi = ({
  usedForWorkflows = false,
  source,
}: UseAccountSubscriptionApiOptions = {}) => {
  const navigate = useUpgradePlanNavigate();
  const { isEmailHubRequired } = useUpgradePlanContext();
  const { refetchCurrentPlan } = useCurrentPlanApi();

  const handleError = () => {
    void navigate({ to: UPGRADE_PATHS.ERROR });
  };

  const upgradeSuccessPath = usedForWorkflows
    ? UPGRADE_PATHS.WORKFLOW_APP_SYNCING
    : UPGRADE_PATHS.UPGRADE_SUCCESSFUL;

  const emailHubSuccessPath = usedForWorkflows
    ? UPGRADE_PATHS.WORKFLOW_APP_SYNCING
    : UPGRADE_PATHS.EMAIL_HUB_SUBSCRIPTION_CHECK;

  const { mutate: updateSubscription } = useMutation({
    mutationFn: ({ planId, planCouponId }: UpdateSubscriptionParams) =>
      accountSubscriptionApi.update(planId, planCouponId, source),
    onSuccess: () => {
      void refetchCurrentPlan();

      if (isEmailHubRequired) {
        createEmailHubSubscription();
      } else {
        void navigate({ to: upgradeSuccessPath });
      }
    },
    onError: handleError,
  });

  const { mutate: createEmailHubSubscription } = useMutation({
    mutationFn: () => accountSubscriptionApi.createEmailHubSubscription(),
    onSuccess: () => {
      void navigate({ to: emailHubSuccessPath });
    },
    onError: handleError,
  });

  return {
    updateSubscription,
    createEmailHubSubscription,
  };
};
